import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Clock,
  Download,
  FileText,
  Filter,
  Layers,
  MoreHorizontal,
  Package,
  Pause,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Smartphone,
  Trash2,
} from "lucide-react";
import { PublishDialog } from "../components/PublishDialog.js";
import { ReleaseDrawer } from "../components/ReleaseDrawer.js";
import type { Platform, ReleaseStatus, ReleaseSummary } from "../contract.js";
import { actorName, formatBytes, formatDateTime } from "../lib/format.js";
import { matchesTerm, useScope } from "../lib/scope.js";
import {
  useDeleteReleaseMutation,
  useHaltReleaseMutation,
  useListAppsQuery,
  useListReleasesQuery,
  useSetRolloutMutation,
} from "../store/api.js";
import { useAppSelector } from "../store/index.js";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

const PAGE_SIZE = 10;

export function ReleasesPage() {
  const role = useAppSelector((state) => state.auth.user?.role);
  const canRelease = role === "admin" || role === "releaser";

  const scope = useScope();
  const apps = useListAppsQuery();
  const page = Number(scope.get("page") || 0);

  const [publishOpen, setPublishOpen] = useState(false);
  const [selectedReleaseId, setSelectedReleaseId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [haltRelease] = useHaltReleaseMutation();
  const [setRollout] = useSetRolloutMutation();
  const [deleteRelease] = useDeleteReleaseMutation();

  const selectedApp = apps.data?.find((a) => a.slug === (scope.app || "digibikes")) ?? apps.data?.[0];
  const channels = selectedApp?.channels ?? [];

  const releases = useListReleasesQuery(
    {
      app: selectedApp?.slug ?? "digibikes",
      channel: scope.channel || undefined,
      platform: (scope.get("platform") as Platform) || undefined,
      limit: 50,
      offset: 0,
    },
    { skip: !selectedApp }
  );

  const rows = releases.data?.releases ?? [];
  const total = releases.data?.total ?? rows.length;

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      return matchesTerm(
        scope.q,
        r.nativeVersion,
        r.releaseNotes,
        r.fingerprint,
        r.createdBy,
        r.channel
      );
    });
  }, [rows, statusFilter, scope.q]);

  const pagedRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;

  // Counts for tabs and stats
  const activeCount = rows.filter((r) => r.status === "active").length;
  const haltedCount = rows.filter((r) => r.status === "halted").length;
  const draftCount = rows.filter((r) => r.status === "draft").length;
  const completedCount = rows.filter((r) => r.rolloutPercent === 100).length;
  const rollbackCount = rows.filter((r) => r.rollbackOfSeq !== null).length;

  const prodCount = rows.filter((r) => r.channel === "production").length;
  const nonProdCount = rows.length - prodCount;
  const activeProdCount = rows.filter((r) => r.status === "active" && r.channel === "production").length;
  const activeNonProdCount = activeCount - activeProdCount;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#101828]">Semua rilis</h1>
          <p className="text-[13.5px] text-[#667085]">
            Kelola seluruh versi aplikasi {selectedApp?.name ?? "Digibikes"} untuk Android dan iOS. Rilis baru dimulai dari staging sebelum ke produksi.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          {canRelease && (
            <Button
              className="h-9 gap-1.5 bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs font-medium shadow-xs"
              onClick={() => setPublishOpen(true)}
            >
              <Plus className="size-4" />
              Buat rilis
            </Button>
          )}
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Total rilis</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {rows.length}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">
            {prodCount} produksi · {nonProdCount} staging/lainnya
          </span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Rilis aktif</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {activeCount}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">
            {activeProdCount} produksi · {activeNonProdCount} non-produksi
          </span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Rollback total</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {rollbackCount}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">
            {rollbackCount > 0 ? `${rollbackCount} versi ditarik` : "Tidak ada rollback"}
          </span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <span className="text-xs font-medium text-[#667085]">Draf & dijeda</span>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {draftCount + haltedCount}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">
            {draftCount} draf · {haltedCount} dijeda
          </span>
        </div>
      </div>

      {/* Filter Tabs & Search Row */}
      <div className="flex flex-col gap-3 rounded-xl border border-[#EAECF0] bg-white p-3.5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Status Pills */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <button
              type="button"
              onClick={() => setStatusFilter("all")}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors ${
                statusFilter === "all"
                  ? "bg-[#0F1B3D] text-white"
                  : "bg-[#F6F7F9] text-[#344054] hover:bg-[#EAECF0]"
              }`}
            >
              Semua <span className="ml-1 opacity-70 font-mono">{rows.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("active")}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors ${
                statusFilter === "active"
                  ? "bg-[#0F1B3D] text-white"
                  : "bg-[#F6F7F9] text-[#344054] hover:bg-[#EAECF0]"
              }`}
            >
              Aktif <span className="ml-1 opacity-70 font-mono">{activeCount}</span>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("completed")}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors ${
                statusFilter === "completed"
                  ? "bg-[#0F1B3D] text-white"
                  : "bg-[#F6F7F9] text-[#344054] hover:bg-[#EAECF0]"
              }`}
            >
              Selesai <span className="ml-1 opacity-70 font-mono">{completedCount}</span>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("halted")}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors ${
                statusFilter === "halted"
                  ? "bg-[#0F1B3D] text-white"
                  : "bg-[#F6F7F9] text-[#344054] hover:bg-[#EAECF0]"
              }`}
            >
              Dijeda <span className="ml-1 opacity-70 font-mono">{haltedCount}</span>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("draft")}
              className={`rounded-lg px-3 py-1.5 font-medium transition-colors ${
                statusFilter === "draft"
                  ? "bg-[#0F1B3D] text-white"
                  : "bg-[#F6F7F9] text-[#344054] hover:bg-[#EAECF0]"
              }`}
            >
              Draf <span className="ml-1 opacity-70 font-mono">{draftCount}</span>
            </button>
          </div>

          {/* Quick refresh */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void releases.refetch()}
            className="h-8 text-xs text-[#667085]"
            title="Segarkan rilis"
          >
            <RefreshCw className={`size-3.5 ${releases.isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Search & Dropdown Filters */}
        <div className="flex flex-col sm:flex-row items-center gap-2.5 pt-2 border-t border-[#EAECF0]">
          <div className="relative flex-1 w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#98A2B3]" />
            <Input
              value={scope.q}
              onChange={(e) => scope.set({ q: e.target.value })}
              placeholder="Cari versi atau catatan rilis…"
              className="h-8.5 pl-9 text-xs bg-[#F6F7F9] border-[#EAECF0] rounded-lg focus-visible:bg-white"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Channel filter */}
            <select
              value={scope.channel}
              onChange={(e) => scope.set({ channel: e.target.value })}
              className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
            >
              <option value="">Semua kanal</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.name}>
                  {ch.name}
                </option>
              ))}
            </select>

            {/* Platform filter */}
            <select
              value={scope.get("platform")}
              onChange={(e) => scope.set({ platform: e.target.value })}
              className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
            >
              <option value="">Semua platform</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
            </select>

            {/* Sort filter */}
            <select
              className="h-8.5 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none"
            >
              <option value="created_desc">Terbaru dibuat</option>
              <option value="version_desc">Versi tertinggi</option>
              <option value="rollout_desc">Rollout tertinggi</option>
            </select>
          </div>
        </div>
      </div>

      {/* Releases Table */}
      <div className="rounded-xl border border-[#EAECF0] bg-white shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] bg-[#F6F7F9]/70 text-[#667085]">
                <th className="py-3 px-4 font-medium">Versi</th>
                <th className="py-3 px-4 font-medium">Platform</th>
                <th className="py-3 px-4 font-medium">Kanal</th>
                <th className="py-3 px-4 font-medium">Ukuran</th>
                <th className="py-3 px-4 font-medium">Rollout</th>
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium">Dibuat Oleh</th>
                <th className="py-3 px-4 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-[#667085]">
                    Tidak ada rilis yang sesuai dengan filter ini.
                  </td>
                </tr>
              ) : (
                pagedRows.map((r) => {
                  const isRollback = r.rollbackOfSeq !== null;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedReleaseId(r.id)}
                      className="hover:bg-[#F6F7F9] cursor-pointer transition-colors"
                    >
                      {/* Versi */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm text-[#101828]">
                            v{r.nativeVersion}
                          </span>
                          <span className="font-mono text-xs text-[#667085]">
                            #{r.releaseSeq}
                          </span>
                          {isRollback && (
                            <span className="inline-flex items-center gap-1 rounded bg-[#FEF3F2] border border-[#FECDCA] px-1.5 py-0.2 text-[10px] font-medium text-[#B42318]">
                              <RotateCcw className="size-2.5" />
                              Rollback
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Platform */}
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium uppercase font-mono ${
                            r.platform === "ios"
                              ? "bg-[#F5F3FF] border border-[#DDD6FE] text-[#6D28D9]"
                              : "bg-[#ECFDF3] border border-[#ABEFC6] text-[#027A48]"
                          }`}
                        >
                          <Smartphone className="size-3" />
                          {r.platform}
                        </span>
                      </td>

                      {/* Kanal */}
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                            r.channel === "production"
                              ? "bg-[#EFF4FF] border border-[#BFDBFE] text-[#2563EB]"
                              : "bg-[#F6F7F9] border border-[#EAECF0] text-[#344054]"
                          }`}
                        >
                          <span
                            className={`size-1.5 rounded-full ${
                              r.channel === "production" ? "bg-[#2563EB]" : "bg-[#98A2B3]"
                            }`}
                          />
                          {r.channel}
                        </span>
                      </td>

                      {/* Ukuran */}
                      <td className="py-3 px-4 font-mono text-[#475467]">
                        {formatBytes(r.sizeBytes)}
                      </td>

                      {/* Rollout */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2.5 min-w-[130px]">
                          <div className="h-1.5 w-24 rounded-full bg-[#EAECF0] overflow-hidden">
                            <div
                              className="h-full rounded-full bg-[#17B26A]"
                              style={{ width: `${r.rolloutPercent}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs text-[#344054] font-medium">
                            {r.rolloutPercent}%
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                            r.status === "active"
                              ? "bg-[#ECFDF3] border border-[#ABEFC6] text-[#027A48]"
                              : r.status === "halted"
                              ? "bg-[#FEF3F2] border border-[#FECDCA] text-[#B42318]"
                              : "bg-[#F6F7F9] border border-[#EAECF0] text-[#667085]"
                          }`}
                        >
                          <span
                            className={`size-1.5 rounded-full ${
                              r.status === "active"
                                ? "bg-[#17B26A]"
                                : r.status === "halted"
                                ? "bg-[#F04438]"
                                : "bg-[#98A2B3]"
                            }`}
                          />
                          {r.status === "active"
                            ? r.rolloutPercent === 100
                              ? "Selesai"
                              : "Aktif"
                            : r.status === "halted"
                            ? "Dijeda"
                            : "Draf"}
                        </span>
                      </td>

                      {/* Dibuat oleh */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="size-6 rounded-full bg-[#0F1B3D] text-white flex items-center justify-center text-[10px] font-bold">
                            {actorName(r.createdBy).slice(0, 2).toUpperCase()}
                          </div>
                          <div className="leading-tight">
                            <span className="block font-medium text-[#101828]">{actorName(r.createdBy)}</span>
                            <span className="block text-[11px] text-[#667085] font-mono">
                              {formatDateTime(r.createdAt).split(",")[0]}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Aksi Dropdown */}
                      <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger className="p-1 rounded-md text-[#98A2B3] hover:text-[#101828] hover:bg-[#EAECF0]">
                            <MoreHorizontal className="size-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44 text-xs">
                            <DropdownMenuItem onSelect={() => setSelectedReleaseId(r.id)}>
                              Buka detail rilis
                            </DropdownMenuItem>
                            {canRelease && (
                              <>
                                <DropdownMenuItem
                                  onSelect={() =>
                                    void setRollout({ id: r.id, rolloutPercent: Math.min(100, r.rolloutPercent + 25) })
                                  }
                                >
                                  Naikkan rollout (+25%)
                                </DropdownMenuItem>
                                {r.status === "active" ? (
                                  <DropdownMenuItem
                                    onSelect={() => void haltRelease({ id: r.id, reason: "Manual halt" })}
                                  >
                                    Jeda rollout
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onSelect={() =>
                                      void setRollout({ id: r.id, rolloutPercent: r.rolloutPercent || 10 })
                                    }
                                  >
                                    Lanjutkan rollout
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-[#F04438]"
                                  onSelect={() => {
                                    if (confirm(`Hapus rilis v${r.nativeVersion}?`)) {
                                      void deleteRelease(r.id);
                                    }
                                  }}
                                >
                                  Hapus rilis
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-[#EAECF0] px-4 py-3 text-xs text-[#667085]">
          <span>
            Menampilkan {filtered.length === 0 ? 0 : page * PAGE_SIZE + 1}–
            {Math.min(filtered.length, (page + 1) * PAGE_SIZE)} dari {filtered.length} rilis
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => scope.set({ page: page > 0 ? page - 1 : undefined })}
              disabled={page === 0}
              className="h-7 text-xs"
            >
              Sebelumnya
            </Button>
            <div className="flex items-center gap-1 px-2">
              <span className="font-semibold text-[#101828]">{page + 1}</span>
              <span>/</span>
              <span>{totalPages}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => scope.set({ page: page + 1 })}
              disabled={page + 1 >= totalPages}
              className="h-7 text-xs"
            >
              Berikutnya
            </Button>
          </div>
        </div>
      </div>

      {/* Release Detail Drawer */}
      {selectedReleaseId && (
        <ReleaseDrawer
          releaseId={selectedReleaseId}
          onClose={() => setSelectedReleaseId(null)}
        />
      )}

      {/* Publish Dialog */}
      {publishOpen && apps.data && (
        <PublishDialog
          apps={apps.data}
          defaultChannel={scope.channel || "production"}
          onClose={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}
